import classnames from 'classnames';
import { useId, FC, MouseEvent } from 'react';
import { graphql, useFragment } from 'react-relay';

import { Link } from 'dibs-elements/exports/Link';
import { AuctionCountdown } from 'dibs-auctions/exports/AuctionCountdown';

import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import { SRC_SET_SIZES_RESP_CAROUSEL_TILE } from '../../../constants/imageConstants';
import { FavoritesProviderChildrenProps } from '../../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';
import { FavoritesItem } from '../../../components/global/FavoriteIcons/FavoritesItem';

// styles
import styles from './PdpRespSimilarItemCarousel.scss';
import { PdpRespSimilarItemCarousel_item$key } from './__generated__/PdpRespSimilarItemCarousel_item.graphql';

type PdpRespSimilarItemCarouselProps = {
    index: number;
    item: PdpRespSimilarItemCarousel_item$key | null;
    isCondensed?: boolean;
    onClick: (index: number, event: MouseEvent | null) => void;
    favorites?: FavoritesProviderChildrenProps;
};

const itemFragment = graphql`
    fragment PdpRespSimilarItemCarousel_item on Item {
        title
        pdpURL
        isArt
        creators {
            creator {
                name
            }
        }
        photos(limit: 1) {
            masterOrZoomPath
            versions {
                webPath
            }
        }
        ...LazyImageWrapper_item
        ...FavoritesItem_item
        ...AuctionCountdown_item @arguments(page: null)
    }
`;
export const PdpRespSimilarItemCarousel: FC<PdpRespSimilarItemCarouselProps> = ({
    index,
    item: itemRef,
    isCondensed,
    onClick = () => {},
    favorites,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const htmlId = useId();
    const { title, pdpURL, isArt, creators, photos } = item || {};
    const creatorName = creators?.[0]?.creator?.name;
    const itemPhoto = photos?.[0];
    const imgSrc = itemPhoto?.versions?.[0]?.webPath || '';
    const masterPath = itemPhoto?.masterOrZoomPath;

    const srcSetAttributes = masterPath
        ? {
              srcSet: getSrcsetString(masterPath),
              sizes: SRC_SET_SIZES_RESP_CAROUSEL_TILE,
          }
        : {};

    const containerClass = classnames(styles.itemContainer, {
        [styles.isCondensed]: !!isCondensed,
    });

    return (
        <div className={containerClass}>
            {favorites && (
                <FavoritesItem
                    wrapperClass={styles.favoritesWrapper}
                    heartIconWrapperClass={styles.favoritesIconWrapper}
                    folderIconWrapperClass={styles.favoritesIconWrapper}
                    {...favorites}
                    item={item}
                />
            )}
            <Link
                href={pdpURL || ''}
                onClick={event => onClick(index, event)}
                id={htmlId}
                underline="none"
                className={styles.overlayLink}
            >
                <div className={styles.overlayLinkSqueeze}>
                    {isArt && creatorName && `${creatorName} - `}
                    {title}
                </div>
            </Link>
            {imgSrc && (
                <div className={styles.imageWrapper}>
                    <img
                        className={styles.image}
                        aria-labelledby={htmlId}
                        src={imgSrc}
                        alt={title || ''}
                        {...srcSetAttributes}
                    />
                </div>
            )}
            {item && <AuctionCountdown item={item} isCompact />}
        </div>
    );
};
